import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Calendar, Plus, Edit, Trash2, ChevronDown, ChevronRight, Loader2, RefreshCw, GraduationCap, Sun, Snowflake, Flower2, Clock, AlertCircle } from "lucide-react";

interface AcademicYear {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  contactDays: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AcademicTerm {
  id: string;
  academicYearId: string;
  name: string;
  shortName: string;
  termNumber: number;
  startDate: string;
  endDate: string;
  createdAt: string;
}

interface TermBreak {
  id: string;
  academicYearId: string;
  termId: string | null;
  name: string;
  type: string;
  startDate: string;
  endDate: string;
  createdAt: string;
}

interface PepWindow {
  id: string;
  academicYearId: string;
  name: string;
  windowType: string;
  startDate: string;
  endDate: string;
  applicableGrades: number[];
  createdAt: string;
}

interface AcademicYearDetail extends AcademicYear {
  terms: AcademicTerm[];
  breaks: TermBreak[];
  pepWindows: PepWindow[];
}

interface CalendarSnapshot {
  academicYear: AcademicYear;
  yearProgress: number;
  currentTerm: {
    term: AcademicTerm;
    dayNumber: number;
    totalDays: number;
    daysRemaining: number;
    progressPercent: number;
    weekNumber: number;
  } | null;
  isBetweenTerms: boolean;
  currentBreak: { break: TermBreak; daysUntil: number; isCurrentlyOnBreak: boolean } | null;
  nextBreak: { break: TermBreak; daysUntil: number; isCurrentlyOnBreak: boolean } | null;
  activePepWindows: { window: PepWindow; daysUntil: number; isActive: boolean }[];
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-JM", {
    timeZone: "America/Jamaica",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateInput(dateStr: string) {
  return new Date(dateStr).toISOString().split("T")[0];
}

const termIcons: Record<string, typeof Sun> = {
  christmas: Snowflake,
  easter: Flower2,
  summer: Sun,
};

const breakTypeLabels: Record<string, string> = {
  "mid-term": "Mid-Term",
  vacation: "Vacation",
  holiday: "Holiday",
};

const windowTypeLabels: Record<string, string> = {
  ability_test: "Ability Test",
  performance_task: "Performance Task",
  gsat: "Grade Six Achievement Test",
};

export default function AcademicCalendarManager() {
  const { toast } = useToast();
  const [expandedYear, setExpandedYear] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ label: string; action: () => void } | null>(null);
  const [editingTerm, setEditingTerm] = useState<AcademicTerm | null>(null);
  const [editingBreak, setEditingBreak] = useState<TermBreak | null>(null);
  const [editingPepWindow, setEditingPepWindow] = useState<PepWindow | null>(null);
  const [addingTermForYear, setAddingTermForYear] = useState<string | null>(null);
  const [addingBreakForYear, setAddingBreakForYear] = useState<string | null>(null);
  const [addingPepWindowForYear, setAddingPepWindowForYear] = useState<string | null>(null);

  const { data: years = [], isLoading: yearsLoading } = useQuery<AcademicYear[]>({
    queryKey: ["/api/admin/academic-years"],
  });

  const { data: snapshot, isLoading: snapshotLoading } = useQuery<CalendarSnapshot>({
    queryKey: ["/api/academic-calendar/current"],
    queryFn: async () => {
      const res = await fetch("/api/academic-calendar/current", {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("Failed to fetch snapshot");
      return res.json();
    },
  });

  const expandedYearId = expandedYear;

  const { data: yearDetail, isLoading: detailLoading } = useQuery<AcademicYearDetail>({
    queryKey: ["/api/admin/academic-years", expandedYearId],
    queryFn: async () => {
      const res = await fetch(`/api/admin/academic-years/${expandedYearId}`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("Failed to fetch year detail");
      return res.json();
    },
    enabled: !!expandedYearId,
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/academic-years/generate-current");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/academic-years"] });
      queryClient.invalidateQueries({ queryKey: ["/api/academic-calendar/current"] });
      toast({ title: "Academic year generated with default calendar" });
    },
    onError: () => {
      toast({ title: "Failed to generate academic year", variant: "destructive" });
    },
  });

  const updateTermMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest("PUT", `/api/admin/academic-terms/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/academic-years"] });
      queryClient.invalidateQueries({ queryKey: ["/api/academic-calendar/current"] });
      setEditingTerm(null);
      toast({ title: "Term updated" });
    },
    onError: () => toast({ title: "Failed to update term", variant: "destructive" }),
  });

  const createTermMutation = useMutation({
    mutationFn: async ({ yearId, data }: { yearId: string; data: any }) => {
      const res = await apiRequest("POST", `/api/admin/academic-years/${yearId}/terms`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/academic-years"] });
      setAddingTermForYear(null);
      toast({ title: "Term created" });
    },
    onError: () => toast({ title: "Failed to create term", variant: "destructive" }),
  });

  const deleteTermMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/academic-terms/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/academic-years"] });
      toast({ title: "Term deleted" });
    },
    onError: () => toast({ title: "Failed to delete term", variant: "destructive" }),
  });

  const updateBreakMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest("PUT", `/api/admin/term-breaks/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/academic-years"] });
      queryClient.invalidateQueries({ queryKey: ["/api/academic-calendar/current"] });
      setEditingBreak(null);
      toast({ title: "Break updated" });
    },
    onError: () => toast({ title: "Failed to update break", variant: "destructive" }),
  });

  const createBreakMutation = useMutation({
    mutationFn: async ({ yearId, data }: { yearId: string; data: any }) => {
      const res = await apiRequest("POST", `/api/admin/academic-years/${yearId}/breaks`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/academic-years"] });
      setAddingBreakForYear(null);
      toast({ title: "Break created" });
    },
    onError: () => toast({ title: "Failed to create break", variant: "destructive" }),
  });

  const deleteBreakMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/term-breaks/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/academic-years"] });
      toast({ title: "Break deleted" });
    },
    onError: () => toast({ title: "Failed to delete break", variant: "destructive" }),
  });

  const updatePepWindowMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest("PUT", `/api/admin/pep-windows/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/academic-years"] });
      queryClient.invalidateQueries({ queryKey: ["/api/academic-calendar/current"] });
      setEditingPepWindow(null);
      toast({ title: "PEP window updated" });
    },
    onError: () => toast({ title: "Failed to update PEP window", variant: "destructive" }),
  });

  const createPepWindowMutation = useMutation({
    mutationFn: async ({ yearId, data }: { yearId: string; data: any }) => {
      const res = await apiRequest("POST", `/api/admin/academic-years/${yearId}/pep-windows`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/academic-years"] });
      setAddingPepWindowForYear(null);
      toast({ title: "PEP window created" });
    },
    onError: () => toast({ title: "Failed to create PEP window", variant: "destructive" }),
  });

  const deletePepWindowMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/pep-windows/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/academic-years"] });
      toast({ title: "PEP window deleted" });
    },
    onError: () => toast({ title: "Failed to delete PEP window", variant: "destructive" }),
  });

  const deleteYearMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/academic-years/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/academic-years"] });
      queryClient.invalidateQueries({ queryKey: ["/api/academic-calendar/current"] });
      if (expandedYear) setExpandedYear(null);
      toast({ title: "Academic year deleted" });
    },
    onError: () => toast({ title: "Failed to delete academic year", variant: "destructive" }),
  });

  if (yearsLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading academic calendar...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {snapshot && !snapshotLoading && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Current Status
            </CardTitle>
            <CardDescription>
              Live snapshot of the academic calendar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Academic Year</p>
                <p className="text-lg font-semibold">{snapshot.academicYear.label}</p>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary rounded-full h-2 transition-all"
                    style={{ width: `${snapshot.yearProgress}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">{snapshot.yearProgress}% complete</p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Current Term</p>
                {snapshot.currentTerm ? (
                  <>
                    <p className="text-lg font-semibold">{snapshot.currentTerm.term.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Week {snapshot.currentTerm.weekNumber} &middot; {snapshot.currentTerm.daysRemaining} days left
                    </p>
                  </>
                ) : (
                  <p className="text-lg font-semibold text-muted-foreground">Between terms</p>
                )}
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  {snapshot.currentBreak ? "Current Break" : "Next Break"}
                </p>
                {(snapshot.currentBreak || snapshot.nextBreak) ? (
                  <>
                    <p className="text-lg font-semibold">
                      {(snapshot.currentBreak || snapshot.nextBreak)!.break.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {snapshot.currentBreak
                        ? "On break now"
                        : `In ${snapshot.nextBreak!.daysUntil} days`}
                    </p>
                  </>
                ) : (
                  <p className="text-lg font-semibold text-muted-foreground">None scheduled</p>
                )}
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">PEP Assessments</p>
                {snapshot.activePepWindows.length > 0 ? (
                  <div className="space-y-1">
                    {snapshot.activePepWindows.slice(0, 2).map((pw) => (
                      <div key={pw.window.id} className="flex items-center gap-2">
                        <Badge variant={pw.isActive ? "default" : "secondary"} className="text-xs">
                          {pw.isActive ? "Active" : `${pw.daysUntil}d`}
                        </Badge>
                        <span className="text-sm truncate">{pw.window.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-lg font-semibold text-muted-foreground">None upcoming</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Academic Years
              </CardTitle>
              <CardDescription>
                Manage academic years, terms, breaks, and PEP assessment windows
              </CardDescription>
            </div>
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Generate Current Year
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {years.length === 0 ? (
            <div className="text-center py-8 space-y-3">
              <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="text-muted-foreground">
                No academic years found. Click "Generate Current Year" to create one with default Jamaican school calendar settings.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {years.map((year) => {
                const isExpanded = expandedYear === year.id;
                return (
                  <div key={year.id} className="border rounded-md">
                    <div
                      className="flex items-center justify-between gap-2 p-3 cursor-pointer hover-elevate"
                      onClick={() => setExpandedYear(isExpanded ? null : year.id)}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">{year.label}</span>
                            {year.isActive && <Badge variant="default" className="text-xs">Active</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(year.startDate)} - {formatDate(year.endDate)} &middot; {year.contactDays} contact days
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDelete({ label: `Delete "${year.label}" and all its terms, breaks, and PEP windows?`, action: () => deleteYearMutation.mutate(year.id) });
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {isExpanded && (
                      <div className="border-t p-4 space-y-6">
                        {detailLoading ? (
                          <div className="flex items-center justify-center py-4 text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Loading details...
                          </div>
                        ) : yearDetail ? (
                          <>
                            <TermsSection
                              terms={yearDetail.terms}
                              yearId={year.id}
                              onEdit={setEditingTerm}
                              onDelete={(id) => deleteTermMutation.mutate(id)}
                              onRequestDelete={(label, action) => setPendingDelete({ label, action })}
                              onAdd={() => setAddingTermForYear(year.id)}
                            />
                            <BreaksSection
                              breaks={yearDetail.breaks}
                              terms={yearDetail.terms}
                              yearId={year.id}
                              onEdit={setEditingBreak}
                              onDelete={(id) => deleteBreakMutation.mutate(id)}
                              onRequestDelete={(label, action) => setPendingDelete({ label, action })}
                              onAdd={() => setAddingBreakForYear(year.id)}
                            />
                            <PepWindowsSection
                              pepWindows={yearDetail.pepWindows}
                              yearId={year.id}
                              onEdit={setEditingPepWindow}
                              onDelete={(id) => deletePepWindowMutation.mutate(id)}
                              onRequestDelete={(label, action) => setPendingDelete({ label, action })}
                              onAdd={() => setAddingPepWindowForYear(year.id)}
                            />
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <EditTermDialog
        term={editingTerm}
        onClose={() => setEditingTerm(null)}
        onSave={(data) => editingTerm && updateTermMutation.mutate({ id: editingTerm.id, data })}
        isPending={updateTermMutation.isPending}
      />

      <AddTermDialog
        yearId={addingTermForYear}
        onClose={() => setAddingTermForYear(null)}
        onSave={(yearId, data) => createTermMutation.mutate({ yearId, data })}
        isPending={createTermMutation.isPending}
      />

      <EditBreakDialog
        brk={editingBreak}
        terms={yearDetail?.terms || []}
        onClose={() => setEditingBreak(null)}
        onSave={(data) => editingBreak && updateBreakMutation.mutate({ id: editingBreak.id, data })}
        isPending={updateBreakMutation.isPending}
      />

      <AddBreakDialog
        yearId={addingBreakForYear}
        terms={yearDetail?.terms || []}
        onClose={() => setAddingBreakForYear(null)}
        onSave={(yearId, data) => createBreakMutation.mutate({ yearId, data })}
        isPending={createBreakMutation.isPending}
      />

      <EditPepWindowDialog
        pepWindow={editingPepWindow}
        onClose={() => setEditingPepWindow(null)}
        onSave={(data) => editingPepWindow && updatePepWindowMutation.mutate({ id: editingPepWindow.id, data })}
        isPending={updatePepWindowMutation.isPending}
      />

      <AddPepWindowDialog
        yearId={addingPepWindowForYear}
        onClose={() => setAddingPepWindowForYear(null)}
        onSave={(yearId, data) => createPepWindowMutation.mutate({ yearId, data })}
        isPending={createPepWindowMutation.isPending}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>{pendingDelete?.label}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { pendingDelete?.action(); setPendingDelete(null); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TermsSection({ terms, yearId, onEdit, onDelete, onRequestDelete, onAdd }: {
  terms: AcademicTerm[];
  yearId: string;
  onEdit: (t: AcademicTerm) => void;
  onDelete: (id: string) => void;
  onRequestDelete: (label: string, action: () => void) => void;
  onAdd: () => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h4 className="font-semibold text-sm flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Terms ({terms.length})
        </h4>
        <Button variant="outline" size="sm" onClick={onAdd}>
          <Plus className="h-3 w-3 mr-1" /> Add Term
        </Button>
      </div>
      {terms.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">No terms defined</p>
      ) : (
        <div className="space-y-1">
          {terms.map((term) => {
            const Icon = termIcons[term.shortName] || Calendar;
            return (
              <div key={term.id} className="flex items-center justify-between gap-2 p-2 rounded-md border">
                <div className="flex items-center gap-3 min-w-0">
                  <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{term.name}</span>
                      <Badge variant="outline" className="text-xs">Term {term.termNumber}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(term.startDate)} - {formatDate(term.endDate)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => onEdit(term)}>
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => { onRequestDelete(`Delete "${term.name}"?`, () => onDelete(term.id)); }}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BreaksSection({ breaks, terms, yearId, onEdit, onDelete, onRequestDelete, onAdd }: {
  breaks: TermBreak[];
  terms: AcademicTerm[];
  yearId: string;
  onEdit: (b: TermBreak) => void;
  onDelete: (id: string) => void;
  onRequestDelete: (label: string, action: () => void) => void;
  onAdd: () => void;
}) {
  const getTermName = (termId: string | null) => {
    if (!termId) return null;
    const term = terms.find((t) => t.id === termId);
    return term?.name || null;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h4 className="font-semibold text-sm flex items-center gap-2">
          <Sun className="h-4 w-4" />
          Breaks & Holidays ({breaks.length})
        </h4>
        <Button variant="outline" size="sm" onClick={onAdd}>
          <Plus className="h-3 w-3 mr-1" /> Add Break
        </Button>
      </div>
      {breaks.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">No breaks defined</p>
      ) : (
        <div className="space-y-1">
          {breaks.map((brk) => (
            <div key={brk.id} className="flex items-center justify-between gap-2 p-2 rounded-md border">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{brk.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {breakTypeLabels[brk.type] || brk.type}
                  </Badge>
                  {getTermName(brk.termId) && (
                    <Badge variant="secondary" className="text-xs">{getTermName(brk.termId)}</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatDate(brk.startDate)}
                  {brk.startDate !== brk.endDate && ` - ${formatDate(brk.endDate)}`}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => onEdit(brk)}>
                  <Edit className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => { onRequestDelete(`Delete "${brk.name}"?`, () => onDelete(brk.id)); }}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PepWindowsSection({ pepWindows, yearId, onEdit, onDelete, onRequestDelete, onAdd }: {
  pepWindows: PepWindow[];
  yearId: string;
  onEdit: (pw: PepWindow) => void;
  onDelete: (id: string) => void;
  onRequestDelete: (label: string, action: () => void) => void;
  onAdd: () => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h4 className="font-semibold text-sm flex items-center gap-2">
          <GraduationCap className="h-4 w-4" />
          PEP Assessment Windows ({pepWindows.length})
        </h4>
        <Button variant="outline" size="sm" onClick={onAdd}>
          <Plus className="h-3 w-3 mr-1" /> Add PEP Window
        </Button>
      </div>
      {pepWindows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">No PEP windows defined</p>
      ) : (
        <div className="space-y-1">
          {pepWindows.map((pw) => (
            <div key={pw.id} className="flex items-center justify-between gap-2 p-2 rounded-md border">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{pw.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {windowTypeLabels[pw.windowType] || pw.windowType}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-xs text-muted-foreground">
                    {formatDate(pw.startDate)} - {formatDate(pw.endDate)}
                  </p>
                  <div className="flex gap-1 flex-wrap">
                    {pw.applicableGrades.map((g) => (
                      <Badge key={g} variant="secondary" className="text-xs">Grade {g}</Badge>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => onEdit(pw)}>
                  <Edit className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => { onRequestDelete(`Delete "${pw.name}"?`, () => onDelete(pw.id)); }}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditTermDialog({ term, onClose, onSave, isPending }: {
  term: AcademicTerm | null;
  onClose: () => void;
  onSave: (data: any) => void;
  isPending: boolean;
}) {
  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [termNumber, setTermNumber] = useState("1");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (term) {
      setName(term.name);
      setShortName(term.shortName);
      setTermNumber(String(term.termNumber));
      setStartDate(formatDateInput(term.startDate));
      setEndDate(formatDateInput(term.endDate));
    }
  }, [term]);

  if (!term) return null;

  return (
    <Dialog open={!!term} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Term</DialogTitle>
          <DialogDescription>Update term details</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Short Name</Label>
              <Select value={shortName} onValueChange={setShortName}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="christmas">Christmas</SelectItem>
                  <SelectItem value="easter">Easter</SelectItem>
                  <SelectItem value="summer">Summer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Term Number</Label>
              <Select value={termNumber} onValueChange={setTermNumber}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave({ name, shortName, termNumber: parseInt(termNumber), startDate, endDate })}
            disabled={isPending || !name || !startDate || !endDate}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddTermDialog({ yearId, onClose, onSave, isPending }: {
  yearId: string | null;
  onClose: () => void;
  onSave: (yearId: string, data: any) => void;
  isPending: boolean;
}) {
  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("christmas");
  const [termNumber, setTermNumber] = useState("1");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  if (!yearId) return null;

  return (
    <Dialog open={!!yearId} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Term</DialogTitle>
          <DialogDescription>Add a new term to this academic year</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Christmas Term" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Short Name</Label>
              <Select value={shortName} onValueChange={setShortName}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="christmas">Christmas</SelectItem>
                  <SelectItem value="easter">Easter</SelectItem>
                  <SelectItem value="summer">Summer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Term Number</Label>
              <Select value={termNumber} onValueChange={setTermNumber}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              onSave(yearId, { name, shortName, termNumber: parseInt(termNumber), startDate, endDate });
              setName("");
              setStartDate("");
              setEndDate("");
            }}
            disabled={isPending || !name || !startDate || !endDate}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Add Term
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditBreakDialog({ brk, terms, onClose, onSave, isPending }: {
  brk: TermBreak | null;
  terms: AcademicTerm[];
  onClose: () => void;
  onSave: (data: any) => void;
  isPending: boolean;
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState("mid-term");
  const [termId, setTermId] = useState<string>("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (brk) {
      setName(brk.name);
      setType(brk.type);
      setTermId(brk.termId || "none");
      setStartDate(formatDateInput(brk.startDate));
      setEndDate(formatDateInput(brk.endDate));
    }
  }, [brk]);

  if (!brk) return null;

  return (
    <Dialog open={!!brk} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Break</DialogTitle>
          <DialogDescription>Update break details</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mid-term">Mid-Term</SelectItem>
                  <SelectItem value="vacation">Vacation</SelectItem>
                  <SelectItem value="holiday">Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Associated Term</Label>
              <Select value={termId} onValueChange={setTermId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {terms.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave({ name, type, termId: termId === "none" ? null : termId, startDate, endDate })}
            disabled={isPending || !name || !startDate || !endDate}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddBreakDialog({ yearId, terms, onClose, onSave, isPending }: {
  yearId: string | null;
  terms: AcademicTerm[];
  onClose: () => void;
  onSave: (yearId: string, data: any) => void;
  isPending: boolean;
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState("mid-term");
  const [termId, setTermId] = useState<string>("none");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  if (!yearId) return null;

  return (
    <Dialog open={!!yearId} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Break</DialogTitle>
          <DialogDescription>Add a new break or holiday</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Mid-Term Break" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mid-term">Mid-Term</SelectItem>
                  <SelectItem value="vacation">Vacation</SelectItem>
                  <SelectItem value="holiday">Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Associated Term</Label>
              <Select value={termId} onValueChange={setTermId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {terms.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              onSave(yearId, { name, type, termId: termId === "none" ? null : termId, startDate, endDate });
              setName("");
              setStartDate("");
              setEndDate("");
            }}
            disabled={isPending || !name || !startDate || !endDate}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Add Break
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditPepWindowDialog({ pepWindow, onClose, onSave, isPending }: {
  pepWindow: PepWindow | null;
  onClose: () => void;
  onSave: (data: any) => void;
  isPending: boolean;
}) {
  const [name, setName] = useState("");
  const [windowType, setWindowType] = useState("ability_test");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [grades, setGrades] = useState<number[]>([]);

  useEffect(() => {
    if (pepWindow) {
      setName(pepWindow.name);
      setWindowType(pepWindow.windowType);
      setStartDate(formatDateInput(pepWindow.startDate));
      setEndDate(formatDateInput(pepWindow.endDate));
      setGrades(pepWindow.applicableGrades);
    }
  }, [pepWindow]);

  if (!pepWindow) return null;

  const toggleGrade = (g: number) => {
    setGrades((prev) => prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g].sort());
  };

  return (
    <Dialog open={!!pepWindow} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit PEP Window</DialogTitle>
          <DialogDescription>Update PEP assessment window details</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Window Type</Label>
            <Select value={windowType} onValueChange={setWindowType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ability_test">Ability Test</SelectItem>
                <SelectItem value="performance_task">Performance Task</SelectItem>
                <SelectItem value="curriculum_test">Curriculum-Based Test</SelectItem>
                <SelectItem value="gsat">Grade Six Achievement Test</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Applicable Grades</Label>
            <div className="flex gap-2 flex-wrap">
              {[1, 2, 3, 4, 5, 6].map((g) => (
                <Button
                  key={g}
                  variant={grades.includes(g) ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleGrade(g)}
                  type="button"
                >
                  Grade {g}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave({ name, windowType, startDate, endDate, applicableGrades: grades })}
            disabled={isPending || !name || !startDate || !endDate || grades.length === 0}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddPepWindowDialog({ yearId, onClose, onSave, isPending }: {
  yearId: string | null;
  onClose: () => void;
  onSave: (yearId: string, data: any) => void;
  isPending: boolean;
}) {
  const [name, setName] = useState("");
  const [windowType, setWindowType] = useState("ability_test");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [grades, setGrades] = useState<number[]>([4, 5, 6, 7]);

  if (!yearId) return null;

  const toggleGrade = (g: number) => {
    setGrades((prev) => prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g].sort());
  };

  return (
    <Dialog open={!!yearId} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add PEP Window</DialogTitle>
          <DialogDescription>Add a new PEP assessment window</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ability Test" />
          </div>
          <div className="space-y-2">
            <Label>Window Type</Label>
            <Select value={windowType} onValueChange={setWindowType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ability_test">Ability Test</SelectItem>
                <SelectItem value="performance_task">Performance Task</SelectItem>
                <SelectItem value="curriculum_test">Curriculum-Based Test</SelectItem>
                <SelectItem value="gsat">Grade Six Achievement Test</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Applicable Grades</Label>
            <div className="flex gap-2 flex-wrap">
              {[1, 2, 3, 4, 5, 6].map((g) => (
                <Button
                  key={g}
                  variant={grades.includes(g) ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleGrade(g)}
                  type="button"
                >
                  Grade {g}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              onSave(yearId, { name, windowType, startDate, endDate, applicableGrades: grades });
              setName("");
              setStartDate("");
              setEndDate("");
            }}
            disabled={isPending || !name || !startDate || !endDate || grades.length === 0}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Add PEP Window
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
